/**
 * Toggle row（ON/OFF 表示付きの行）。
 *
 * 設計書 spec §7（Perimeter Text の Enable / Guides トグル）/ FR-15 近傍。
 * wireframe `wireframes/v0.10/wireframe.html` L48–51 の `.toggle-row`
 * マークアップを踏襲する:
 *
 * ```
 * <div class="toggle-row active">
 *   <span>Label</span>
 *   <span class="state">ON</span>
 * </div>
 * ```
 *
 * `active` クラスがあると accent 色の枠＋赤文字、なければ通常枠。モノトーン
 * 原則に従い、チェックボックスではなく枠色の変化のみで状態を表現する。
 */

import type { AppState, Store } from '../../state';

/**
 * `AppState` の中で boolean 型のフィールド名だけを抽出する補助型。
 * 誤って number / string フィールドをバインドするのを型で防ぐ。
 */
type BooleanField = {
  [K in keyof AppState]: AppState[K] extends boolean ? K : never;
}[keyof AppState];

/** `mountToggleRow` のオプション。 */
export interface ToggleRowOptions {
  /** 左側に表示するラベル（例: `"Enable"` / `"Show Cells"`）。 */
  label: string;
  /** `AppState` の bind 先 boolean フィールド。 */
  field: BooleanField;
}

/**
 * Toggle row をマウントする。
 *
 * @param root - セクションのコンテナ
 * @param store - グローバルストア
 * @param options - label / field
 * @returns teardown 関数
 */
export function mountToggleRow(
  root: HTMLElement,
  store: Store<AppState>,
  options: ToggleRowOptions,
): () => void {
  const row = document.createElement('div');
  row.className = 'toggle-row';
  // role / aria は checkbox 相当。キーボードフォーカスと SPACE/ENTER でも
  // 切り替え可能にする（button 要素を使うと周囲の .btn スタイルが混ざる
  // ため div + tabindex + role="switch" を採用）。
  row.setAttribute('role', 'switch');
  row.setAttribute('tabindex', '0');

  const labelSpan = document.createElement('span');
  labelSpan.textContent = options.label;

  const stateSpan = document.createElement('span');
  stateSpan.className = 'state';

  row.appendChild(labelSpan);
  row.appendChild(stateSpan);

  /** `active` クラスと ON/OFF テキスト、aria-checked を state に同期。 */
  const sync = (on: boolean): void => {
    row.classList.toggle('active', on);
    row.setAttribute('aria-checked', on ? 'true' : 'false');
    stateSpan.textContent = on ? 'ON' : 'OFF';
  };

  sync(store.get()[options.field]);

  const toggle = (): void => {
    const current = store.get()[options.field];
    store.set({ [options.field]: !current } as Partial<AppState>);
  };

  const onClick = (): void => {
    toggle();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      toggle();
    }
  };

  row.addEventListener('click', onClick);
  row.addEventListener('keydown', onKey);

  const unsubscribe = store.subscribe((state, prev) => {
    if (state[options.field] !== prev[options.field]) {
      sync(state[options.field]);
    }
  });

  root.appendChild(row);

  return (): void => {
    unsubscribe();
    row.removeEventListener('click', onClick);
    row.removeEventListener('keydown', onKey);
    row.remove();
  };
}
